import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Chip, ChipGroup } from "../../ui";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { useHubBasePath } from "../../../utils/hubPaths";
import s from "../clinic.module.css";
import aa from "./adminApp.module.css";

function HubChevronBack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14.5 6.5L9 12l5.5 5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** iOS-style nav bar: back + title (+ optional right action). */
export function HubNav({
  title,
  onBack,
  right,
  backLabel = "Назад",
  showBack = true,
}) {
  const navigate = useNavigate();
  const hubBase = useHubBasePath();
  const handleBack = onBack ?? (() => navigate(`${hubBase}/more`));

  return (
    <header className={aa.hubNav}>
      <div className={aa.hubNavBar}>
        {showBack ? (
          <button
            type="button"
            className={aa.hubNavBack}
            onClick={handleBack}
            aria-label={backLabel}
          >
            <HubChevronBack />
          </button>
        ) : (
          <span className={aa.hubNavSideSpacer} aria-hidden />
        )}
        <h1 className={aa.hubNavTitle}>{title}</h1>
        <div className={aa.hubNavRight}>{right ?? <span className={aa.hubNavSideSpacer} aria-hidden />}</div>
      </div>
    </header>
  );
}

export function HubNavAdd({ onClick, ariaLabel = "Добавить" }) {
  return (
    <button
      type="button"
      className={aa.hubNavAddBtn}
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 5v14M5 12h14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

/** PWA hub page shell: sticky nav + padded body. */
export function HubScreen({
  title,
  onBack,
  right,
  className = "",
  bodyClassName = "",
  children,
}) {
  return (
    <div className={`${aa.hubPage} ${aa.hubPageBleed} ${className}`.trim()}>
      <HubNav title={title} onBack={onBack} right={right} />
      <div className={`${aa.hubPageBody} ${aa.hubPageBodyAnimated} ${bodyClassName}`.trim()}>{children}</div>
    </div>
  );
}

/** Корневая вкладка PWA (Клиенты, Календарь, Клуб) — шапка как в настройках, без «Назад». */
export function HubTabScreen({
  title,
  right,
  className = "",
  bodyClassName = "",
  children,
}) {
  return (
    <div className={`${aa.hubPage} ${aa.hubPageBleed} ${className}`.trim()}>
      <HubNav title={title} showBack={false} right={right} />
      <div className={`${aa.hubPageBody} ${aa.hubPageBodyAnimated} ${bodyClassName}`.trim()}>{children}</div>
    </div>
  );
}

export function HubToolbar({ countLabel, onAdd, addAriaLabel = "Добавить" }) {
  return (
    <div
      className={`${aa.hubActionBar} ${
        countLabel && onAdd ? aa.hubActionBarStart : ""
      }`}
    >
      {countLabel ? <span className={aa.hubCount}>{countLabel}</span> : null}
      {onAdd ? (
        <button
          type="button"
          className={aa.clientsAddBtn}
          onClick={onAdd}
          aria-label={addAriaLabel}
          title={addAriaLabel}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

/** Detail screen: back to list (not «Ещё»). */
export function HubBack({ title, onBack }) {
  return <HubNav title={title} onBack={onBack} backLabel="К списку" />;
}

export function HubListGroup({ children }) {
  return <div className={aa.hubCardList}>{children}</div>;
}

export function HubRow({
  title,
  meta,
  sub,
  active,
  onClick,
  badge,
  badgeTone,
  progress,
}) {
  const badgeClass =
    badgeTone === "warn"
      ? aa.hubRowBadgeWarn
      : badgeTone === "danger"
        ? aa.hubRowBadgeDanger
        : badgeTone === "ok"
          ? aa.hubRowBadgeOk
          : badgeTone === "accent"
            ? aa.hubRowBadgeAccent
            : aa.hubRowBadge;

  const interactive = typeof onClick === "function";

  return (
    <article className={`${aa.hubRow} ${active ? aa.hubRowActive : ""}`.trim()}>
      <button
        type="button"
        className={`${aa.hubRowBtn} ${interactive ? "" : aa.hubRowBtnStatic}`.trim()}
        onClick={interactive ? onClick : undefined}
        disabled={!interactive}
      >
        <span className={aa.hubRowBody}>
          <span className={aa.hubRowTop}>
            <strong className={aa.hubRowName}>{title}</strong>
            {badge ? <span className={badgeClass}>{badge}</span> : null}
          </span>
          {meta ? <span className={aa.hubRowMeta}>{meta}</span> : null}
          {sub ? <span className={aa.hubRowSub}>{sub}</span> : null}
          {progress != null ? (
            <span className={aa.hubProgress} aria-hidden>
              <span className={aa.hubProgressFill} style={{ width: `${progress}%` }} />
            </span>
          ) : null}
        </span>
        {interactive ? <span className={aa.hubRowChevron} aria-hidden /> : null}
      </button>
    </article>
  );
}

export function HubKpiStrip({ items, className = "" }) {
  if (!items?.length) return null;
  return (
    <div className={`${aa.hubKpiStrip} ${className}`.trim()}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={
            item.active ? `${aa.hubKpiCell} ${aa.hubKpiCellActive}` : aa.hubKpiCell
          }
          onClick={item.onClick}
          disabled={!item.onClick}
        >
          <span
            className={item.warn ? `${aa.hubKpiVal} ${aa.hubKpiValWarn}` : aa.hubKpiVal}
          >
            {item.value}
          </span>
          <span className={aa.hubKpiLbl}>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

export function HubSegment({ tabs, active, onChange, className = "" }) {
  if (!tabs?.length) return null;

  const scrollable = tabs.length >= 3;

  return (
    <div className={`${aa.hubSegmentWrap} ${className}`.trim()}>
      <div
        className={[
          aa.hubSegment,
          scrollable ? aa.hubSegmentScroll : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="tablist"
        aria-label="Вкладки"
      >
        {tabs.map((t) => {
          const selected = active === t.id;
          const ariaLabel = t.ariaLabel || t.label || undefined;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-label={ariaLabel}
              title={ariaLabel}
              className={[
                selected ? aa.hubSegmentBtnActive : aa.hubSegmentBtn,
                t.iconOnly ? aa.hubSegmentBtnIconOnly : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onChange(t.id)}
            >
              {t.iconOnly ? t.icon : (
                <>
                  {t.icon ? (
                    <span className={aa.hubSegmentTabIcon}>{t.icon}</span>
                  ) : null}
                  {t.label}
                  {t.count != null ? (
                    <span className={aa.hubSegmentCount}>{t.count}</span>
                  ) : null}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Переключатель «Активные / Архив» со счётчиком, без подчёркнутых вкладок. */
export function StatusArchiveTabs({
  value,
  onChange,
  archiveCount = 0,
  variant = "inline",
  className = "",
}) {
  const tabs = [
    { id: "active", label: "Активные" },
    { id: "archive", label: "Архив", count: archiveCount },
  ];
  const bar = variant === "bar";
  return (
    <div className={bar ? aa.hubSegmentWrap : undefined}>
      <div
        className={[
          aa.statusArchiveTabs,
          bar ? aa.statusArchiveTabsBar : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        role="tablist"
        aria-label="Активные или архив"
      >
        {tabs.map((t) => {
          const selected = value === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={selected ? aa.statusArchiveTabOn : aa.statusArchiveTab}
              onClick={() => onChange(t.id)}
            >
              {t.label}
              {t.count != null ? (
                <span className={aa.statusArchiveCount}>{t.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Компактные чипы-фильтры (не как основные вкладки HubSegment). */
export function HubFilterChips({
  tabs,
  active,
  onChange,
  className = "",
  scroll,
}) {
  if (!tabs?.length) return null;

  const useScroll = scroll ?? tabs.length > 6;
  const chips = (
    <ChipGroup label="Фильтр" className={className}>
      {tabs.map((t) => (
        <Chip
          key={t.id}
          selected={String(active) === String(t.id)}
          count={t.count}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </Chip>
      ))}
    </ChipGroup>
  );

  if (useScroll) {
    return <div className={aa.hubChipScroll}>{chips}</div>;
  }
  return chips;
}

const SHEET_MS = 260;
const SHEET_EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const SHEET_CLOSE_EASE = "cubic-bezier(0.4, 0, 0.6, 1)";
const DRAG_MIN_CLOSE = 56;
const DRAG_CLOSE_RATIO = 0.18;
const FLING_VELOCITY = 0.35;

/** Нижняя шторка (портал в body — не обрезается контейнером страницы). */
export function HubSheet({
  open,
  onClose,
  title,
  subtitle,
  titleId,
  header,
  children,
  footer,
  sheetClassName = "",
  bodyClassName = "",
}) {
  const isMobile = useIsMobile(768);
  const [visible, setVisible] = useState(Boolean(open));
  const [offsetY, setOffsetY] = useState(0);
  const [withTransition, setWithTransition] = useState(false);
  const [dragging, setDragging] = useState(false);

  const panelRef = useRef(null);
  const offsetRef = useRef(0);
  const closingRef = useRef(false);
  const dragRef = useRef(null);
  const closeTimerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const visibleRef = useRef(Boolean(open));
  const openRef = useRef(open);

  onCloseRef.current = onClose;
  visibleRef.current = visible;
  openRef.current = open;

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const panelHeight = () =>
    panelRef.current?.offsetHeight || Math.round(window.innerHeight * 0.9);

  const runClose = useCallback(
    (notifyParent, fromOffset) => {
      if (closingRef.current) return;
      closingRef.current = true;
      clearCloseTimer();

      const start = Math.max(0, fromOffset ?? offsetRef.current);
      offsetRef.current = start;
      setOffsetY(start);
      setWithTransition(true);
      setDragging(false);
      dragRef.current = null;

      requestAnimationFrame(() => {
        const h = panelHeight();
        offsetRef.current = h;
        setOffsetY(h);
      });

      closeTimerRef.current = setTimeout(() => {
        closingRef.current = false;
        setVisible(false);
        setWithTransition(false);
        offsetRef.current = 0;
        setOffsetY(0);
        dragRef.current = null;
        if (notifyParent) onCloseRef.current?.();
      }, SHEET_MS);
    },
    [],
  );

  useEffect(() => {
    if (!open) {
      if (!visibleRef.current || closingRef.current) return undefined;
      if (isMobile) {
        runClose(false);
      } else {
        setVisible(false);
        offsetRef.current = 0;
        setOffsetY(0);
      }
      return undefined;
    }

    closingRef.current = false;
    clearCloseTimer();

    if (!isMobile) {
      setVisible(true);
      offsetRef.current = 0;
      setOffsetY(0);
      return undefined;
    }

    const alreadyOpen =
      visibleRef.current &&
      offsetRef.current <= 12 &&
      !closingRef.current;

    setVisible(true);
    setDragging(false);

    if (alreadyOpen) return undefined;

    setWithTransition(false);
    const startY = window.innerHeight;
    offsetRef.current = startY;
    setOffsetY(startY);

    let alive = true;
    let innerId = 0;
    const enterId = requestAnimationFrame(() => {
      innerId = requestAnimationFrame(() => {
        if (!alive || !openRef.current) return;
        setWithTransition(true);
        offsetRef.current = 0;
        setOffsetY(0);
      });
    });

    return () => {
      alive = false;
      cancelAnimationFrame(enterId);
      if (innerId) cancelAnimationFrame(innerId);
    };
  }, [open, isMobile, runClose]);

  useEffect(() => {
    if (!open || !isMobile || !visible) return undefined;
    const t = window.setTimeout(() => {
      if (!openRef.current || closingRef.current) return;
      if (offsetRef.current > window.innerHeight * 0.2) {
        setWithTransition(true);
        offsetRef.current = 0;
        setOffsetY(0);
      }
    }, SHEET_MS + 80);
    return () => window.clearTimeout(t);
  }, [open, isMobile, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    document.documentElement.classList.add("hub-sheet-open");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.classList.remove("hub-sheet-open");
      document.body.style.overflow = prev;
    };
  }, [visible]);

  useEffect(() => () => clearCloseTimer(), []);

  const dismiss = useCallback(() => {
    if (!isMobile) {
      setVisible(false);
      onCloseRef.current?.();
      return;
    }
    runClose(true);
  }, [isMobile, runClose]);

  const onDragStart = (e) => {
    if (closingRef.current || e.button !== 0) return;
    if (e.target.closest("button, input, select, textarea, a, label")) return;

    dragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startOffset: offsetRef.current,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
    };
    setDragging(true);
    setWithTransition(false);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onDragMove = (e) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    const dy = e.clientY - drag.startY;
    const next = Math.max(0, drag.startOffset + dy);
    const now = performance.now();
    const dt = now - drag.lastT;
    if (dt > 0) {
      drag.velocity = (e.clientY - drag.lastY) / dt;
    }
    drag.lastY = e.clientY;
    drag.lastT = now;
    offsetRef.current = next;
    setOffsetY(next);

    if (next > 0 && e.cancelable) e.preventDefault();
  };

  const onDragEnd = (e) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setDragging(false);

    const offset = offsetRef.current;
    const h = panelHeight();
    const threshold = Math.max(DRAG_MIN_CLOSE, h * DRAG_CLOSE_RATIO);
    const flingDown = drag.velocity > FLING_VELOCITY;

    if (offset >= threshold || flingDown) {
      runClose(true, offset);
      return;
    }

    setWithTransition(true);
    offsetRef.current = 0;
    setOffsetY(0);
  };

  if (!visible) return null;

  const id = titleId || "hub-sheet-title";

  const sheetHeader =
    header ?? (
      <header className={aa.hubSheetHead}>
        <div className={aa.hubSheetHeadText}>
          <h3 id={id}>{title}</h3>
          {subtitle ? <p className={aa.hubSheetSubtitle}>{subtitle}</p> : null}
        </div>
        <button
          type="button"
          className={aa.hubSheetCloseBtn}
          onClick={() => dismiss()}
          aria-label="Закрыть"
        >
          ×
        </button>
      </header>
    );

  if (!isMobile) {
    return createPortal(
      <div
        data-hub-overlay="1"
        className={`${aa.hubSheetBackdrop} ${aa.hubSheetBackdropCenter}`}
        onClick={() => dismiss()}
        role="presentation"
      >
        <div
          className={`${aa.hubSheet} ${aa.hubSheetCenter} ${sheetClassName}`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={id}
          onClick={(e) => e.stopPropagation()}
        >
          {sheetHeader}
          <div
            data-hub-overlay-body="1"
            className={`${aa.hubSheetBody} ${bodyClassName}`.trim()}
          >
            {children}
          </div>
          {footer ? <footer className={aa.hubSheetFoot}>{footer}</footer> : null}
        </div>
      </div>,
      document.body,
    );
  }

  const h = panelHeight();
  const backdropAlpha = Math.max(0.12, 0.48 * (1 - Math.min(offsetY, h) / h));

  return createPortal(
    <div
      data-hub-overlay="1"
      className={aa.hubSheetBackdrop}
      style={{
        backgroundColor: `rgba(15, 23, 42, ${backdropAlpha})`,
        transition: withTransition && !dragging
          ? `background-color ${SHEET_MS}ms ease`
          : "none",
      }}
      onClick={() => dismiss()}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={`${aa.hubSheet} ${
          dragging || offsetY > 0 ? aa.hubSheetDragging : ""
        } ${sheetClassName}`.trim()}
        style={{
          transform: `translate3d(0, ${offsetY}px, 0)`,
          transition:
            withTransition && !dragging
              ? `transform ${SHEET_MS}ms ${closingRef.current ? SHEET_CLOSE_EASE : SHEET_EASE}`
              : "none",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={aa.hubSheetDragHeader}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <div className={aa.hubSheetDragZone}>
            <div className={aa.hubSheetHandle} aria-hidden />
          </div>
          {sheetHeader}
        </div>
        <div
          data-hub-overlay-body="1"
          className={`${aa.hubSheetBody} ${bodyClassName}`.trim()}
        >
          {children}
        </div>
        {footer ? <footer className={aa.hubSheetFoot}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export function HubFormCard({ title, children }) {
  return (
    <section className={aa.hubFormCard}>
      {title ? <h3 className={aa.hubFormCardTitle}>{title}</h3> : null}
      {children}
    </section>
  );
}

export function HubDetailPanel({ children }) {
  return <div className={aa.hubDetailPanel}>{children}</div>;
}

export function HubSectionTitle({ children }) {
  return <h3 className={aa.hubSectionLabel}>{children}</h3>;
}

/** Кнопки внизу шторки: основное действие + «Отмена». */
export function HubSheetActions({
  primary,
  secondaryLabel = "Отмена",
  onSecondary,
  primaryForm,
  primaryType = "submit",
  primaryDisabled = false,
  onPrimary,
}) {
  return (
    <>
      <button
        type={primaryType}
        form={primaryForm}
        disabled={primaryDisabled}
        className={aa.hubSheetBtnPrimary}
        onClick={onPrimary}
      >
        {primary}
      </button>
      <button
        type="button"
        className={aa.hubSheetBtnGhost}
        onClick={onSecondary}
      >
        {secondaryLabel}
      </button>
    </>
  );
}

/** Центрированная модалка на десктопе (без стилей нижней шторки). */
export function DesktopDialog({
  open,
  onClose,
  onBack,
  backLabel = "Назад",
  title,
  subtitle,
  titleId,
  children,
  footer,
  wide = false,
  bodyClassName = "",
  dialogClassName = "",
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const id = titleId || "desktop-dialog-title";

  return createPortal(
    <div
      data-hub-overlay="1"
      className={aa.hubDialogBackdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`${aa.hubDialog} ${wide ? aa.hubDialogWide : ""} ${dialogClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={aa.hubDialogHead}>
          {onBack ? (
            <button
              type="button"
              className={aa.hubDialogBack}
              onClick={onBack}
            >
              ← {backLabel}
            </button>
          ) : null}
          <div className={aa.hubDialogHeadText}>
            <h2 id={id}>{title}</h2>
            {subtitle ? (
              <p className={aa.hubDialogSubtitle}>{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            className={aa.hubDialogClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>
        <div
          data-hub-overlay-body="1"
          className={`${aa.hubDialogBody} ${bodyClassName}`.trim()}
        >
          {children}
        </div>
        {footer ? (
          <footer className={aa.hubDialogFoot}>
            <div className={aa.hubDialogFootActions}>{footer}</div>
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
